import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import * as actions from 'containers/ProjectTools/Notes/actions';
import NoteGroup from './NoteGroup';
import NoteGroupAdd from './NoteGroup.Add';
import Note from './Note';

const Notes = ({
  items,
  view,
  groups,
  projectId,
  error,
  notesEdit,
  searchKey,
  addNewGroup,
  setAddNewGroup,
}) => {
  const [itemGroups, setItemGroups] = useState(items);
  const [draggingNote, setDraggingNote] = useState(null);

  useEffect(() => {
    setItemGroups(items);
  }, [items, groups, error]);

  const dispatch = useDispatch();
  const {
    reorderNotes,
    createNoteGroup,
    renameNoteGroup,
    deleteNoteGroup,
    reorderNoteGroup,
    updateNoteGroupView,
  } = bindActionCreators(actions, dispatch);

  const goToNote = (noteId, url) => {
    if (url) {
      window.open(url, '_blank');
    }
    else {
      window.location.href = `/app/project/${projectId}/tools/notes/${noteId}`;
    }
  };

  const updateGroupView = (groupId, expand) => {
    const noteGroupsView = groups.map(item => {
      if (item.id === groupId) {
        return { id: item.id, expand };
      }
      return { id: item.id, expand: item.expand };
    });
    updateNoteGroupView({ weddingId: projectId, groupId, groupExpand: expand, noteGroupsView });
  };

  const sortFn = (asc = false) => {
    return (a, b) => {
      if (a.order > b.order) return asc ? 1 : -1;
      if (a.order < b.order) return asc ? -1 : 1;
      return 0;
    };
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const onDragStart = ({ active }) => {
    if(!notesEdit){
      return;
    }
    setDraggingNote(itemGroups.find(i => i.id === active.id));
  };

  const handleDragOver = ({ active, over }) => {
    const overId = over?.id;
    const activeContainer = active.data.current.sortable?.containerId;
    const overContainer = over?.data.current?.sortable?.containerId || overId;

    // over.data.current will be null if dragging into a collapse group.
    // To prevent weird jumpiness when dragging an item into a collapsed group.
    if (!over.data.current) {
      setDraggingNote(itemGroups.find(i => i.id === active.id));
      return;
    }

    if (activeContainer !== overContainer) {
      const overItem = itemGroups.find((i) => i.id === overId);
      const groupId = overItem ? overItem.groupId : overId === 'nogroup' ? null : overId;
      if (overItem || groupId) {
        const maxOrder = Math.max(...(itemGroups.filter(el => el.groupId === groupId).map(el => el.order)));
        setItemGroups(itemGroups.map(el => el.id === active.id ? { ...el, groupId, order: maxOrder + 1 }
          : el));
      }
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if(!notesEdit){
      return;
    }
    const overId = over?.id;
    if (!overId) {
      return;
    }

    let order;
    let groupId;
    let index = 0;

    // drop over note
    if (over.data.current) {
      order = itemGroups.find((item) => item.id === over.id)?.order;
      groupId = itemGroups.find((item) => item.id === active.id)?.groupId;
      setItemGroups(
        [...itemGroups]
          .map((item) => (item.id === active.id ? { ...item, order } : item))
          .sort(sortFn(true))
          .map((item) => {
            let sortedItem = item;
            if (item.groupId === groupId) {
              index = order === index ? index += 1 : index;
              if (item.id !== active.id) {
                sortedItem = { ...item, order: index };
                index += 1;
              }
            }
            return sortedItem;
          }));
    } else {
      // drop over group
      order = 0;
      groupId = over.id === 'nogroup' ? null : over.id;
      setItemGroups([...itemGroups].map(item => item.id === active.id ? { ...item, order, groupId } : item));
    }
    reorderNotes({ id: active.id, order, groupId, weddingId: projectId });
    setDraggingNote(null);
  };

  const renderNoteGroup = () => {
    return (
      <>
        <NoteGroup
          viewType={containerClass}
          notes={itemGroups
            .filter((item) => item.groupId === null)
            .sort(sortFn())}
          goToNote={goToNote}
          createNoteGroup={createNoteGroup}
          key="nogroup"
          id="nogroup"
          isDragging={draggingNote && !draggingNote?.groupId}
          notesEdit={notesEdit}
          addMode={addNewGroup}
          setAddMode={setAddNewGroup}
        />

        <div className="group-list">
          {groups.map((group, index) => {
            return (
              <React.Fragment key={group.id}>
                <NoteGroup
                  key={group.id}
                  id={group.id}
                  groupIndex={index}
                  name={group.name}
                  order={group.order}
                  expand={group.expand}
                  renameNoteGroup={renameNoteGroup}
                  deleteNoteGroup={deleteNoteGroup}
                  reorderNoteGroup={reorderNoteGroup}
                  updateGroupView={updateGroupView}
                  createNoteGroup={createNoteGroup}
                  viewType={containerClass}
                  notes={itemGroups
                  .filter((item) => item.groupId === group.id)
                  .sort(sortFn())}
                  goToNote={goToNote}
                  isGroup
                  isLast={index === groups.length - 1}
                  isDragging={draggingNote?.groupId === group.id}
                  notesEdit={notesEdit}
                  searchKey={searchKey}
                />
                <NoteGroupAdd
                  createNoteGroup={createNoteGroup}
                  weddingId={projectId}
                  index={items.length + index}
                  groupId={group.id}
                  notesEdit={notesEdit}
                  addMode={addNewGroup && !itemGroups.filter((item) => item.groupId === null).length && index === 0}
                  setAddMode={setAddNewGroup}
                />
              </React.Fragment>
            );
          })
          }
        </div>
      </>
    );
  };

  const containerClass = view === 'grid' ? 'notes-list--grid' : '';
  return (
    <>
      { notesEdit ? (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
        >
          {renderNoteGroup()}
          <div className={containerClass}>
            <DragOverlay
              dropAnimation={null}
            >
              {draggingNote ? (
                <Note
                  {...itemGroups.find(element => element.id === draggingNote.id)}
                  style={{
                    cursor: 'grabbing',
                    opacity: 0.5,
                  }}
                />
                ) : null}
            </DragOverlay>
          </div>
        </DndContext>
      ) : renderNoteGroup()}
    </>
  );
};

export default Notes;
